import { compose, filter, isNil, path, pathOr, pluck, prop, propOr, reject, uniq } from 'ramda';
import { createSelector } from 'reselect';

import { getCduUser, getCduUserPermissions } from '@gef-ui/cdu-user/selectors';

import { NAME } from './constants';
import { isSameOrUndefined } from './utils';

const empty = [];

export const getPermissions = getCduUserPermissions;
export const getUser = compose(prop('userLogon'), getCduUser);
export const getFirstName = compose(prop('firstName'), getCduUser);
export const getSurname = compose(prop('surname'), getCduUser);
export const getUserFullName = compose(prop('userFullName'), getCduUser);
export const getUserOid = compose(prop('oid'), getCduUser);

export const getInfo = (state) => state[NAME];
export const getCurrentRole = compose(prop('currentRole'), getInfo);
const getAllowedCampaigns = compose(propOr(empty, 'allowedCampaigns'), getInfo);
export const getHasSubordinates = compose(prop('hasSubordinates'), getInfo);
export const getHasKmpPpzSubordinates = compose(prop('hasKmpPpzSubordinates'), getInfo);
export const getHasKmpSellerSubordinates = compose(prop('hasKmpSellerSubordinates'), getInfo);
export const getHasPPZSubordinates = compose(prop('hasPPZSubordinates'), getInfo);
export const getIsPPZSubordinate = compose(prop('ppzsubordinate'), getInfo);
export const getRoleMap = compose(propOr(empty, 'roleStructureMap'), getInfo);
export const getUserFetched = compose(prop('userFetched'), getInfo);

export const getIsEDKSubSeller = createSelector(getInfo, propOr(false, 'isEDKSubSeller'));

export const getAllowedCampaignsByRole = createSelector(getAllowedCampaigns, getCurrentRole, (campaigns, role) =>
	campaigns.filter(({ role: cRole }) => cRole === role),
);

export const getRolesByCurrentRole = createSelector(getRoleMap, getCurrentRole, (roles, currentRole) =>
	roles.filter((r) => r.role === currentRole),
);

// reduces array of organization structures to one structure with no unique values
export const getRoleStructure = createSelector(
	getRolesByCurrentRole,
	compose(
		(structure) => ({
			role: structure.role,
			channelId: structure.channelId,
			regionId: isNil(structure.channelId) ? undefined : structure.regionId,
			agencyId: isNil(structure.regionId) ? undefined : structure.agencyId,
			managerLogin: isNil(structure.agencyId) ? undefined : structure.managerLogin,
			sellerLogin: isNil(structure.managerLogin) ? undefined : structure.sellerLogin,
			ppzLogin: isNil(structure.sellerLogin) ? undefined : structure.ppzLogin,
		}),
		(structures) =>
			structures.reduce((acc, value) => ({
				role: acc.role,
				channelId: isSameOrUndefined('channelId', acc, value),
				regionId: isSameOrUndefined('regionId', acc, value),
				agencyId: isSameOrUndefined('agencyId', acc, value),
				managerLogin: isSameOrUndefined('managerLogin', acc, value),
				sellerLogin: isSameOrUndefined('sellerLogin', acc, value),
				ppzLogin: isSameOrUndefined('ppzLogin', acc, value),
			})),
	),
);

export const getRoles = createSelector(getRoleMap, compose(uniq, pluck('role')));

export const getChannelIds = createSelector(getRoleMap, compose(reject(isNil), uniq, pluck('channelId')));

export const getAllowedTaskSearchIds = createSelector([getPermissions], (permissions) => {
	const listOfPermittedTaskTypes = path(['1111', 'values', 'taskTypes', 0, 'list'], permissions);
	// if there is a nested "list" attr, return it for further processing

	if (isNil(listOfPermittedTaskTypes)) {
		const permittedTaskTypesWithoutList = pathOr([], ['1111', 'values', 'taskTypes'], permissions);
		return permittedTaskTypesWithoutList;
	} else {
		return listOfPermittedTaskTypes;
	}
});

const getAllowedStructureByType = (type) => createSelector(getRolesByCurrentRole, compose(reject(isNil), pluck(type)));

export const getAllowedRegionsByChannel = createSelector(
	(_, channelId) => channelId,
	getRolesByCurrentRole,
	(channelId, roleMap) => {
		const regionsByChannel = compose(
			reject(isNil),
			pluck('regionId'),
			filter((role) => role.channelId === channelId),
		)(roleMap);

		return regionsByChannel;
	},
);

export const getHasPPZSubordinatesOrIsPPZSubordinate = createSelector(
	getIsPPZSubordinate,
	getHasPPZSubordinates,
	(isPPZSubordinate, hasPPZSubordinates) => {
		return isPPZSubordinate || hasPPZSubordinates;
	},
);

const byCampaignId = (id) => (item) => !id || id === item.campaignWaveId;

export const getAllowedRegionsWithCampaign = createSelector(
	(_, campaignId) => campaignId,
	(_, campaignId, channelId) => channelId,
	getRolesByCurrentRole,
	(campaignId, channelId, rolesByCurrentRole) => {
		const allowedRegionsWithCampaignId = compose(
			reject(isNil),
			pluck('regionId'),
			filter(byCampaignId(Number(campaignId))),
			filter((role) => role.channelId === channelId),
		)(rolesByCurrentRole);

		return allowedRegionsWithCampaignId;
	},
);

export const getAllowedRegionsOnlyByCampaignId = createSelector(
	(_, campaignId) => campaignId,
	getRolesByCurrentRole,
	(campaignId, rolesByCurrentRole) => {
		const allowedRegionsBasedOnCampaignId = compose(
			reject(isNil),
			pluck('regionId'),
			filter(byCampaignId(Number(campaignId))),
		)(rolesByCurrentRole);

		return allowedRegionsBasedOnCampaignId;
	},
);

export const getAllowedChannelsWithCampaign = createSelector(
	(_, campaignId) => campaignId,
	getRolesByCurrentRole,
	(campaignId, rolesByCurrentRole) => {
		const allowedChannelsWithCampaignId = compose(
			reject(isNil),
			pluck('channelId'),
			filter(byCampaignId(Number(campaignId))),
		)(rolesByCurrentRole);

		return allowedChannelsWithCampaignId;
	},
);

export const getAllowedChannels = getAllowedStructureByType('channelId');
export const getAllowedRegions = getAllowedStructureByType('regionId');
export const getAllowedAgencies = getAllowedStructureByType('agencyId');
export const getAllowedManagers = getAllowedStructureByType('managerLogin');
export const getAllowedSellers = getAllowedStructureByType('sellerLogin');
export const getAllowedPPZ = getAllowedStructureByType('ppzLogin');
